import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, BookOpen, FileText, CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Books = () => {
  const booksStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "CIRO Exam Study Guides & Textbooks",
    "description": "Printed study guides and textbooks for CIRO exam preparation",
    "itemListElement": [
      {
        "@type": "Book",
        "position": 1,
        "name": "CIRE Exam Study Guide",
        "author": ["Dr. Amina Beecroft", "Harry Vadalkar"],
        "numberOfPages": "700",
        "bookFormat": "Paperback"
      },
      {
        "@type": "Book",
        "position": 2,
        "name": "RSE Exam Study Guide",
        "author": ["Dr. Amina Beecroft", "Harry Vadalkar"],
        "numberOfPages": "700",
        "bookFormat": "Paperback"
      },
      {
        "@type": "Book",
        "position": 3,
        "name": "Derivatives Exam Study Guide",
        "author": ["Dr. Amina Beecroft", "Harry Vadalkar"],
        "bookFormat": "Paperback",
        "offers": {
          "@type": "Offer",
          "price": "299.99",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock",
          "url": "https://a.co/d/0c7dhbo9"
        }
      },
      {
        "@type": "Book",
        "position": 4,
        "name": "Traders Licensing Exam Study Guide",
        "author": ["Dr. Amina Beecroft", "Harry Vadalkar"],
        "bookFormat": "Paperback",
        "offers": {
          "@type": "Offer",
          "price": "299.99",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock",
          "url": "https://a.co/d/0c400Tnw"
        }
      }
    ]
  };

  const books = [
    {
      exam: 'CIRE',
      available: true,
      books: [
        {
          title: 'CIRE Interactive Quick Study Guide',
          subtitle: 'Online Interactive Learning',
          features: '9 Interactive Elements',
          price: '34.99',
          originalPrice: '99.99',
          description: 'Master the Canadian Investment Regulatory Examination with our revolutionary interactive study system. 9 comprehensive elements with visual learning tools, practice questions, and real-time feedback.',
          link: '/books/cire-interactive',
          isInternal: true,
          badges: ['Online', 'Visual Learning', 'Lifetime Access', '9 Interactive Elements'],
          isNew: true,
          discount: 'April Special: 65% Off'
        },
        {
          title: 'CIRE Mock Exam Pack (Online)',
          subtitle: '5 Full-Length Interactive Mock Exams',
          features: '550 Questions with Rationales',
          price: '49.99',
          description: 'Test your readiness with 5 complete CIRE mock exams online. 550 questions with comprehensive rationales for every question and answer. Practice mode & timed exam mode included.',
          link: '/books/cire-mock-exam-pack',
          isInternal: true,
          badges: ['Online', '5 Mock Exams', 'Detailed Rationales', 'Lifetime Access'],
          isNew: true
        },
        {
          title: 'CIRE Element-Wise Question Bank (Online)',
          subtitle: '9 Targeted Element Practice Banks',
          features: 'Comprehensive Questions for All 9 Elements',
          price: '49.99',
          description: 'Master each CIRE element with focused practice questions. 9 comprehensive question banks with detailed rationales for every question. Target weak areas element by element.',
          link: '/books/cire-element-bank',
          isInternal: true,
          badges: ['Online', '9 Element Banks', 'Detailed Rationales', 'Lifetime Access'],
          isNew: true
        },
        {
          title: 'Printed CIRE Study Guide',
          subtitle: 'Complete 700 Page Book',
          pages: '700',
          price: '299.99',
          description: 'Comprehensive exam-focused study guide covering all 9 CIRO syllabus elements. Designed for aspiring Registered Representatives (RRs) and Investment Representatives (IRs).',
          link: 'https://www.amazon.ca/dp/B0FV3J7PJJ',
          features: ['All 9 CIRO Elements', 'Case Studies', 'Exam Strategies', 'By Dr. Amina Beecroft & Harry Vadalkar']
        },
        {
          title: 'CIRE 5 Full-Length Mock Exams',
          subtitle: 'Printed Mock Exams',
          questions: '550',
          price: '49.99',
          description: 'Practice with 5 full-length mock exams (550 questions) designed to mirror the official CIRE exam in format, difficulty, and timing.',
          link: 'https://www.amazon.ca/dp/B0FWK36ZND',
          features: ['5 Complete Practice Tests', '550 Questions Total', 'Detailed Explanations', 'Realistic Exam Format']
        },
        {
          title: 'CIRE Quick Study Guide',
          subtitle: 'Short Exam Guide - Top 200 Concepts',
          concepts: '200',
          price: '34.99',
          description: 'High-yield revision guide distilling the Top 200 most testable CIRE concepts. Perfect for final week review.',
          link: 'https://www.amazon.ca/dp/B0GC5Z4RVM',
          features: ['Top 200 Concepts', 'Fast Review', 'Exam Traps Highlighted', 'Key Formulas & Ethics Rules']
        }
      ]
    },
    {
      exam: 'RSE',
      available: true,
      books: [
        {
          title: 'RSE Interactive Quick Study Guide',
          subtitle: 'Online Interactive Learning',
          features: '9 Elements + Mock Exam',
          price: '49.99',
          originalPrice: '129.99',
          description: 'Master the Registered Securities Exam with our cutting-edge interactive study system. 9 comprehensive elements with reading notes, games, flashcards, and quizzes covering all RSE topics, plus a 120-question mock exam.',
          link: '/books/rse-interactive',
          isInternal: true,
          badges: ['Online', 'Visual Learning', 'Lifetime Access', '9 Elements + Mock Exam'],
          isNew: true,
          discount: 'April Special: 62% Off'
        },
        {
          title: 'Printed RSE Study Guide',
          subtitle: 'Complete 780 Page Book',
          pages: '780',
          price: '299.99',
          description: 'Comprehensive study guide for the CIRO Retail Securities Exam (RSE), built using the Financial Doctor Methodology with real client scenarios.',
          link: 'https://www.amazon.ca/dp/B0G3L3TCJR',
          features: ['780 Pages', 'Case-Driven Methodology', 'KYC & Suitability Focus', 'Portfolio Construction']
        },
        {
          title: 'RSE Mock Exam Pack (Online)',
          subtitle: '5 Full-Length Interactive Mock Exams',
          features: '550 Questions with Rationales',
          price: '49.99',
          description: 'Test your readiness with 5 complete RSE mock exams online. 550 questions with comprehensive rationales for every question and answer. Practice mode & timed exam mode included.',
          link: '/books/rse-mock-exam-pack',
          isInternal: true,
          badges: ['Online', '5 Mock Exams', 'Detailed Rationales', 'Lifetime Access'],
          isNew: true
        },
        {
          title: 'RSE Element-Wise Question Bank (Online)',
          subtitle: '9 Targeted Element Practice Banks',
          features: 'Comprehensive Questions for All 9 Elements',
          price: '49.99',
          description: 'Master each RSE element with focused practice questions. 9 comprehensive question banks with detailed rationales for every question.',
          link: '/books/rse-element-bank',
          isInternal: true,
          badges: ['Online', '9 Element Banks', 'Detailed Rationales', 'Lifetime Access'],
          isNew: true
        },
        {
          title: 'RSE 5 Full-Length Mock Exams',
          subtitle: 'Printed Mock Exams',
          questions: '600+',
          price: '49.99',
          description: 'Prepare with 5 full-length mock exams (600+ questions) that replicate the RSE exam in structure, difficulty, and competency level.',
          link: 'https://www.amazon.ca/dp/B0GG6TS3DQ',
          features: ['5 Complete Practice Tests', '600+ Questions', 'Competency-Based', 'Detailed Rationales']
        },
        {
          title: 'RSE Quick Study Guide',
          subtitle: 'Short Exam Guide - Top 300 Concepts',
          concepts: '300',
          price: '34.99',
          description: 'Top 300 Concepts guide distilling the RSE syllabus into the most testable, high-probability topics.',
          link: 'https://www.amazon.ca/dp/B0GC55S3HZ',
          features: ['Top 300 Concepts', 'Fast-Track Revision', 'CIRO Elements 1-9', 'Last-Week Review Tool']
        }
      ]
    },
    {
      exam: 'ISE',
      available: true,
      books: [
        {
          title: 'ISE Interactive Quick Study Guide',
          subtitle: 'Online Interactive Learning',
          features: '7 Elements + Mock Exam',
          price: '49.99',
          originalPrice: '129.99',
          description: 'Master CIRO Institutional Securities Exam with our interactive study system. 7 comprehensive elements covering institutional capital markets, client relationships, and portfolio management, plus mock exam with diagnostics.',
          link: '/books/ise-interactive',
          isInternal: true,
          badges: ['Online', 'Visual Learning', 'Lifetime Access', '7 Elements + Mock Exam'],
          isNew: true,
          discount: 'April Special: 62% Off'
        },
        {
          title: 'Printed ISE Study Guide',
          subtitle: 'Complete Study Book',
          description: 'Comprehensive exam-focused study guide for the CIRO Investment Services Exam (ISE). Covers all essential topics for aspiring investment professionals.',
          link: 'https://a.co/d/4QD7Dva',
          price: '299.99',
          features: ['All ISE Topics', 'Case Studies', 'Exam Strategies', 'By Dr. Amina Beecroft & Harry Vadalkar']
        },
        {
          title: 'ISE Full-Length Mock Exams',
          subtitle: 'Printed Mock Exams',
          description: 'Practice with full-length mock exams designed to mirror the official ISE exam in format, difficulty, and timing.',
          link: 'https://www.amazon.ca/stores/Harry-Vadalkar/author/B0FB7D5BBD?ref=ap_rdr&shoppingPortalEnabled=true',
          features: ['Complete Practice Tests', 'Detailed Explanations', 'Realistic Exam Format', 'Competency-Based']
        },
        {
          title: 'ISE Quick Study Guide',
          subtitle: 'Short Exam Guide',
          description: 'High-yield revision guide distilling the most testable ISE concepts. Perfect for final week review.',
          link: 'https://www.amazon.ca/stores/Harry-Vadalkar/author/B0FB7D5BBD?ref=ap_rdr&shoppingPortalEnabled=true',
          features: ['Top Concepts', 'Fast Review', 'Exam Traps Highlighted', 'Key Formulas']
        }
      ]
    },
    {
      exam: 'Derivatives',
      available: true,
      books: [
        {
          title: 'Derivatives Interactive Quick Study Guide',
          subtitle: 'Online Interactive Learning',
          features: '8 Elements + Mock Exam',
          price: '49.99',
          originalPrice: '129.99',
          description: 'Master CIRO Derivatives exam with our interactive study system. 8 comprehensive elements covering options, futures, swaps, and risk management, plus mock exam with diagnostics.',
          link: '/books/derivatives-interactive',
          isInternal: true,
          badges: ['Online', 'Visual Learning', 'Lifetime Access', '8 Elements + Mock Exam'],
          isNew: true,
          discount: 'April Special: 62% Off'
        },
        {
          title: 'Printed Derivatives Study Guide',
          subtitle: 'Complete Study Book',
          description: 'Comprehensive study guide for the CIRO Derivatives Exam. Covers options, futures, forwards, swaps, and all derivative instruments tested on the exam.',
          link: 'https://a.co/d/0c7dhbo9',
          price: '299.99',
          features: ['All Derivatives Topics', 'Options & Futures', 'Exam Strategies', 'By Dr. Amina Beecroft & Harry Vadalkar']
        },
        {
          title: 'Derivatives Mock Exams',
          subtitle: 'Printed Mock Exams',
          description: 'Practice with full-length mock exams that replicate the Derivatives exam in structure and difficulty.',
          link: 'https://www.amazon.ca/stores/Harry-Vadalkar/author/B0FB7D5BBD?ref=ap_rdr&shoppingPortalEnabled=true',
          features: ['Complete Practice Tests', 'Detailed Rationales', 'Realistic Exam Format', 'Competency-Based']
        },
        {
          title: 'Derivatives Quick Study Guide',
          subtitle: 'Short Exam Guide',
          description: 'Top concepts guide distilling the Derivatives syllabus into the most testable, high-probability topics.',
          link: 'https://www.amazon.ca/stores/Harry-Vadalkar/author/B0FB7D5BBD?ref=ap_rdr&shoppingPortalEnabled=true',
          features: ['Top Concepts', 'Fast-Track Revision', 'Last-Week Review Tool', 'Key Formulas']
        }
      ]
    },
    {
      exam: 'Traders',
      available: true,
      isNew: true,
      books: [
        {
          title: 'Printed Traders Exam Study Guide',
          subtitle: 'Complete Study Book - 10 CIRO Elements',
          description: 'Comprehensive study guide for the CIRO Traders Licensing Examination. Covers all 10 elements including trading rules, marketplaces, trade execution, derivatives requirements, and compliance.',
          link: 'https://a.co/d/0c400Tnw',
          price: '299.99',
          features: ['All 10 CIRO Elements', 'Trading Rules & Methods', 'Marketplaces & Derivatives', 'By Dr. Amina Beecroft & Harry Vadalkar'],
          isNew: true
        },
        {
          title: 'Traders Exam Video Course',
          subtitle: 'Online Video + Practice - Coming Soon',
          features: 'Video Lectures, Podcast, Games, Mock Exams',
          price: '199.00',
          description: 'Complete video course preparation for the Traders Licensing Examination with online book, video lectures, podcast, interactive games, practice questions, and mock exams. One-time payment for lifetime access.',
          link: '/traders-exam',
          isInternal: true,
          badges: ['Coming Soon', 'One-Time $199', 'Video Course', 'Lifetime Access'],
          isNew: true
        }
      ]
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="CIRO Exam Study Guides & Books 2025 - Amazon | 700+ Page Textbooks & Mock Exams"
        description="Get printed CIRO exam study guides on Amazon. 700+ page textbooks for CIRE & RSE exams. Full mock exams, quick study guides. Written by Dr. Amina Beecroft & Harry Vadalkar. Covers all 9 CIRO elements. Shipped across Canada - Alberta, Ontario, BC & all provinces."
        keywords="CIRE study guide, CIRE book, CIRE textbook, CIRE notes, CIRE cheat sheet, RSE study guide, RSE book, RSE textbook, RSE notes, CIRO exam books, CIRO study materials, CIRO mock exam book, CIRO practice exam book, CIRO question bank, ISE study guide, Derivatives study guide, CIRO exam prep book Amazon, printed CIRO materials, CIRO exam books Canada, buy CIRE book, buy RSE book"
        ogTitle="CIRO Exam Prep Books - Study Guides, Mock Exams & Quick Reviews"
        ogDescription="Printed study guides, mock exams, and quick review books for all CIRO certifications. 700+ pages. Available on Amazon Canada."
        structuredData={booksStructuredData}
      />

      {/* Header Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '24px',
            color: 'white'
          }}>
            CIRO Exam Prep Books
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            maxWidth: '800px',
            margin: '0 auto',
            opacity: 0.95
          }}>
            Comprehensive printed study guides, full-length mock exams, and quick study notes written by expert instructors
          </p>
          <p style={{
            fontSize: '1.125rem',
            opacity: 0.9
          }}>
            Available on Amazon • 10% bulk discount on 5+ copies
          </p>
        </div>
      </section>


      {/* Books by Exam */}
      {books.map((examSection, idx) => (
        <section key={idx} style={{
          padding: '96px 0',
          backgroundColor: idx % 2 === 0 ? 'white' : 'var(--neutral-light)'
        }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                <h2 className="heading-2" style={{ margin: 0 }}>
                  {examSection.exam} Exam Study Materials
                </h2>
                {examSection.isNew && (
                  <span style={{
                    backgroundColor: '#0d9488',
                    color: 'white',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em'
                  }}>
                    NEW
                  </span>
                )}
              </div>
              {!examSection.available && (
                <div style={{
                  display: 'inline-block',
                  backgroundColor: 'var(--gold-primary)',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontWeight: 700
                }}>
                  COMING SOON
                </div>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '40px'
            }}>
              {examSection.books.map((book, bookIdx) => (
                <div key={bookIdx} style={{
                  backgroundColor: 'white',
                  border: book.comingSoon ? '2px dashed var(--border-medium)' : book.isNew ? '3px solid var(--gold-primary)' : '2px solid var(--border-light)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  opacity: book.comingSoon ? 0.6 : 1,
                  position: 'relative'
                }} className={!book.comingSoon ? 'card-hover-effect' : ''}>
                  {/* NEW Badge */}
                  {book.isNew && (
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      backgroundColor: 'var(--gold-primary)',
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      letterSpacing: '0.05em',
                      zIndex: 10,
                      boxShadow: '0 2px 8px rgba(212,175,55,0.3)'
                    }}>
                      NEW
                    </div>
                  )}
                  {/* Book Image Placeholder */}
                  <div style={{
                    height: '280px',
                    background: book.comingSoon 
                      ? 'linear-gradient(135deg, var(--border-light), var(--neutral-light))'
                      : 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <img 
                      src="/images/features/books.jpg"
                      alt={book.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.2
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      color: 'white',
                      textAlign: 'center'
                    }}>
                      <BookOpen size={60} style={{ marginBottom: '12px' }} />
                      <div style={{ fontSize: '1.5rem', fontWeight: 900 }}>
                        {book.pages && `${book.pages} Pages`}
                        {book.questions && `${book.questions} Questions`}
                        {book.concepts && `Top ${book.concepts}`}
                        {book.comingSoon && 'Coming Soon'}
                        {!book.pages && !book.questions && !book.concepts && !book.comingSoon && examSection.exam}
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: '32px' }}>
                    <h3 className="heading-4" style={{ marginBottom: '8px', color: 'var(--brand-primary)' }}>
                      {book.title}
                    </h3>
                    <p style={{ 
                      fontSize: '1rem', 
                      color: 'var(--text-secondary)',
                      marginBottom: '16px',
                      fontWeight: 600
                    }}>
                      {book.subtitle}
                    </p>

                    {book.link && (
                      <>
                        <p className="body-small" style={{ marginBottom: '24px', lineHeight: 1.6 }}>
                          {book.description}
                        </p>

                        {/* Badges for interactive book */}
                        {book.badges && (
                          <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {book.badges.map((badge, bIdx) => (
                              <span key={bIdx} style={{
                                backgroundColor: '#D4AF3720',
                                color: '#B8860B',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                border: '1px solid #D4AF3740'
                              }}>
                                {badge}
                              </span>
                            ))}
                          </div>
                        )}

                        {book.features && (
                          <div style={{ marginBottom: '24px' }}>
                            {typeof book.features === 'string' ? (
                              <p style={{
                                backgroundColor: '#D4AF3715',
                                color: '#B8860B',
                                padding: '10px 14px',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                border: '1px solid #D4AF3730',
                                textAlign: 'center'
                              }}>
                                {book.features}
                              </p>
                            ) : (
                              <>
                                <p className="caption" style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
                                  KEY FEATURES:
                                </p>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                  {book.features.map((feature, fIdx) => (
                                    <li key={fIdx} style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '12px',
                                      marginBottom: '8px'
                                    }}>
                                      <CheckCircle size={18} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                                      <span className="body-small">{feature}</span>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        )}

                        {book.price && (
                          <div style={{
                            padding: '20px',
                            backgroundColor: 'var(--neutral-light)',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            textAlign: 'center'
                          }}>
                            <p className="caption" style={{ marginBottom: '8px' }}>
                              {book.isInternal ? 'PRICE' : 'AMAZON PRICE'}
                            </p>
                            {book.originalPrice && (
                              <div style={{ marginBottom: '8px' }}>
                                <span style={{
                                  fontSize: '1.2rem',
                                  color: 'var(--text-secondary)',
                                  textDecoration: 'line-through',
                                  fontWeight: 600
                                }}>
                                  CAD ${book.originalPrice}
                                </span>
                              </div>
                            )}
                            <p style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: book.discount ? '8px' : '0' }}>
                              CAD ${book.price}
                            </p>
                            {book.discount && (
                              <div style={{
                                display: 'inline-block',
                                backgroundColor: '#D4AF3720',
                                color: '#B8860B',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                border: '1px solid #D4AF3740'
                              }}>
                                🔥 {book.discount}
                              </div>
                            )}
                          </div>
                        )}

                        {book.isInternal ? (
                          <Link 
                            to={book.link}
                            className="btn-primary" 
                            style={{ width: '100%', textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}
                          >
                            Explore Interactive Book
                          </Link>
                        ) : (
                          <a 
                            href={book.link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn-primary" 
                            style={{ width: '100%' }}
                          >
                            {book.price ? 'Buy on Amazon' : 'View on Amazon'} <ExternalLink size={20} />
                          </a>
                        )}
                      </>
                    )}

                    {book.comingSoon && (
                      <div style={{
                        padding: '60px 20px',
                        textAlign: 'center'
                      }}>
                        <FileText size={48} color="var(--text-muted)" style={{ marginBottom: '16px' }} />
                        <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                          This book is currently in development and will be available soon.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Bulk Orders Section */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>
            Bulk & Institutional Orders
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '16px',
            maxWidth: '700px',
            margin: '0 auto 16px',
            opacity: 0.95
          }}>
            <strong>10% discount on orders of 5+ copies</strong> (shipped separately)
          </p>
          <p className="body-large" style={{ 
            marginBottom: '40px',
            maxWidth: '700px',
            margin: '0 auto 40px',
            opacity: 0.95
          }}>
            Institutional packages available for training programs
          </p>
          <a href="mailto:info@vittafinance.ca" className="btn-gold">
            Email for Bulk Orders
          </a>
        </div>
      </section>
    </div>
  );
};

export default Books;
